import React from 'react'
import { hydrateRoot, type Root } from 'react-dom/client'
import { BoundedContent, ScreenClassProvider, SwanI18nProvider } from '@vp/swan'
import type { MountWithRootOptions } from '@vp/ubik-fragment-types'

import SearchBar from '../components/full-header/search-bar'
import { NPAbsoluteGuaranteeLogo } from '../components/full-footer/sections/AbsoluteGuaranteeSection'
import { FooterNavigation } from '../components/full-footer/sections/FooterNavigation'
import { IdentityProvider, LoggerProvider, UserContextProvider } from '@vp/ubik-context'
import { VatContextProvider } from '../context/VatContext'
import { TenantConfigProvider } from '../context/TenantConfigContext'
import { VatToggle } from '../components/full-header/VatToggle'
import GeoIpBar from '../components/full-header/geo-ip-bar'
import { CountrySelectorButton } from '../components/full-footer/sections/CountrySelectorButton'
import VatSelectionBar from '../components/full-header/VatSelectionBar'
import Cart from '../components/full-header/Cart'
import { createAuthConfig } from '../utils/auth-config'
import { MyAccount } from '../components/full-header/my-account'
import { I18nProvider } from '../context/TranslationContext'
import { loadLocale } from '../localization/translations'
import MobileMenu from '../components/full-header/mobile-menu'
import { DesktopNavigation } from '../components/full-header/navigation/desktop'
import { fetchNavigationByVariation } from '../clients/navigation.client'
import LanguageBar from '../components/full-header/language-bar'
import { getFooterTracker, getHeaderTracker, initTracking, type TrackLinkOptions } from '../tracking'
import { ComponentNameMap, ComponentType } from '../types/components-map'
import type { WebSSRProps } from '../types/ssr-props'
import type { Locale } from '../types/localization'
import { type MountId, MountIds, VatPlacement } from '../constants/hydration'
import { DEFAULT_LOCALE } from '../constants/config'
import FavoritesHeader from '../components/full-header/header-links/FavoritesHeader'
import { Logger } from '../utils/logger'
import { GoogleOneTapWrapper } from '../components/full-header/GoogleOneTapWrapper'
import { ErrorBoundary } from '../components/common/ErrorBoundary'
import MyProjects from '../components/full-header/header-links/MyProjects'
import NavContextProvider from '../context/NavContextProvider'
import HelpCenter from '../components/full-header/header-links/HelpCenter'
import { PillarCarousel } from '../components/brand-pillars/PillarCarousel'
import { DEFAULT_CAROUSEL_A11Y, SWAN_ADAPTIVE_CAROUSEL_TRANSLATIONS } from '../constants/brand-pillars'
import type { TenantConfig } from '../types/tenant-config'

/** Registry to track hydration roots by container ID for proper cleanup and re-renders */
const hydrationRoots: Map<string, Root> = new Map()

/**
 * Safely retrieves a DOM element by ID for hydration
 */
function getHydrationMount(id: MountId): HTMLElement | null {
  return document.getElementById(id)
}

/**
 * Hydrates a component and tracks the root for cleanup.
 * If the container already has a root, uses render() instead of hydrateRoot().
 * Includes error handlers for production debugging of hydration mismatches.
 */
function hydrateAndTrack(container: HTMLElement, element: React.ReactNode): void {
  const containerId = container.id

  const existingRoot = hydrationRoots.get(containerId)
  if (existingRoot) {
    // Container already has a root, use render() to update
    existingRoot.render(element as React.ReactElement)
    return
  }

  const root = hydrateRoot(container, element, {
    onCaughtError: (error: unknown, errorInfo: { componentStack?: string }) => {
      const err = error instanceof Error ? error : new Error(String(error))
      const cause = 'cause' in err ? err.cause : undefined
      Logger.instance.error('[bookends-v2] Hydration onCaughtError', {
        error: err.message,
        cause,
        componentStack: errorInfo.componentStack,
        containerId,
      })
    },
    onUncaughtError: (error: unknown, errorInfo: { componentStack?: string }) => {
      const err = error instanceof Error ? error : new Error(String(error))
      const cause = 'cause' in err ? err.cause : undefined
      Logger.instance.error('[bookends-v2] Hydration onUncaughtError', {
        error: err.message,
        cause,
        componentStack: errorInfo.componentStack,
        containerId,
      })
    },
    onRecoverableError: (error: unknown) => {
      Logger.instance.warning('[bookends-v2] Hydration onRecoverableError', {
        error: error instanceof Error ? error.message : String(error),
        containerId,
      })
    },
  })
  hydrationRoots.set(containerId, root)
}

/**
 * Wraps a component with ScreenClassProvider for responsive layout support
 */
function withScreenClass(children: React.ReactNode): React.ReactElement {
  return <ScreenClassProvider>{children}</ScreenClassProvider>
}

/**
 * Wraps a component with authentication provider
 */
function withAuth(locale: string, children: React.ReactNode): React.ReactElement {
  return <IdentityProvider auth={createAuthConfig(locale)}>{children}</IdentityProvider>
}

/**
 * Wraps a component with i18n provider
 */
function withI18n(locale: string, children: React.ReactNode): React.ReactElement {
  return <I18nProvider defaultLocale={locale as Locale}>{children}</I18nProvider>
}

/**
 * Wraps a component with the Ubik user context provider.
 *
 * Each hydrateAndTrack call creates an independent hydrateRoot; React context does not
 * cross root boundaries, so these roots do not inherit the UserContextProvider that Ubik
 * supplies on the server or via clientMount. Components relying on @vp/my-account-pages-dropdown
 * (whose MyAccountResourcesContentProvider calls useUserContext) must therefore provide it here.
 * Without it, hydrateRoot throws "useUserContext must be used within a UserContextProvider",
 * React discards the subtree, and the mount point is left empty.
 */
function withUserContext(ssrProps: WebSSRProps, children: React.ReactNode): React.ReactElement {
  const { tenant, locale, userContext } = ssrProps
  return <UserContextProvider value={{ userContext: { ...userContext, tenant, locale }, trackedUserContext: [] }}>{children}</UserContextProvider>
}

function withTenantConfig(tenantConfig: TenantConfig | null, children: React.ReactNode): React.ReactElement {
  return <TenantConfigProvider value={tenantConfig ?? null}>{children}</TenantConfigProvider>
}

/**
 * Determines component type based on component name
 */
function getComponentType(componentName: ComponentNameMap): ComponentType {
  return [ComponentNameMap.FULL_HEADER, ComponentNameMap.SLIM_HEADER].includes(componentName) ? ComponentType.HEADER : ComponentType.FOOTER
}

/**
 * Schedules hydration using requestIdleCallback when available, falls back to immediate execution
 */
function scheduleIdleHydration(callback: () => void, timeout = 1000): void {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout })
  } else {
    callback()
  }
}

/**
 * Hydrate Google One Tap component
 */

function hydrateGoogleOneTap(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.GOOGLE_ONE_TAP)
  if (!mount) return

  const { tenant, userContext } = ssrProps
  if (!userContext || !tenant) return

  hydrateAndTrack(
    mount,
    withAuth(
      locale,
      <UserContextProvider value={{ userContext: { tenant, ...userContext }, trackedUserContext: [] }}>
        <LoggerProvider>
          <ErrorBoundary componentName="GoogleOneTap" suppressError>
            <GoogleOneTapWrapper />
          </ErrorBoundary>
        </LoggerProvider>
      </UserContextProvider>,
    ),
  )
}

/**
 * Hydrates the GeoIP bar for country-based messaging
 */
function hydrateGeoIpBar(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.GEO_IP_BAR)
  if (!mount) return

  const { currentCountry, tenant, tenantConfig } = ssrProps
  hydrateAndTrack(
    mount,
    withScreenClass(withTenantConfig(tenantConfig ?? null, withI18n(locale, <GeoIpBar locale={locale} currentCountry={currentCountry} tenant={tenant} />))),
  )
}

/**
 * Hydrates the VAT selection bar for tax display preferences
 */
function hydrateVatSelectionBar(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.VAT_SELECTION_BAR)
  if (!mount) return

  const { vatToggleLabel, vatToggleInclusionText, vatToggleExclusionText } = ssrProps
  hydrateAndTrack(
    mount,
    withScreenClass(
      <VatContextProvider vatToggleLabel={vatToggleLabel} vatToggleInclusionText={vatToggleInclusionText} vatToggleExclusionText={vatToggleExclusionText}>
        <VatSelectionBar locale={locale} />
      </VatContextProvider>,
    ),
  )
}

/**
 * Hydrates the language bar for locale selection
 */
function hydrateLanguageBar(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.LANGUAGE_BAR)
  if (!mount) return

  const { tenantConfig } = ssrProps
  hydrateAndTrack(mount, withScreenClass(withTenantConfig(tenantConfig ?? null, withI18n(locale, <LanguageBar locale={locale} />))))
}

/**
 * Hydrates the VAT toggle in the brand banner
 */
function hydrateVatToggle(ssrProps: WebSSRProps): void {
  const mount = getHydrationMount(MountIds.VAT_TOGGLE)
  if (!mount) return

  const { vatToggleLabel, vatToggleInclusionText, vatToggleExclusionText, locale } = ssrProps
  hydrateAndTrack(
    mount,
    <VatContextProvider vatToggleLabel={vatToggleLabel} vatToggleInclusionText={vatToggleInclusionText} vatToggleExclusionText={vatToggleExclusionText}>
      <VatToggle vatPlacement={VatPlacement.BRAND_BANNER} locale={locale} />
    </VatContextProvider>,
  )
}

/**
 * Hydrates the search bar with typeahead functionality
 */
function hydrateSearchBar(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.SEARCH_BAR)
  if (!mount) return

  const { forwardPath = '', searchLabel = '', userContext, isExpandedSearchEnabled, tenant } = ssrProps
  if (!userContext || !tenant) return

  hydrateAndTrack(
    mount,
    withI18n(
      locale,
      <UserContextProvider value={{ userContext: { tenant, ...userContext }, trackedUserContext: [] }}>
        <SearchBar locale={locale} forwardedPath={forwardPath} searchLabel={searchLabel} isExpandedSearchEnabled={isExpandedSearchEnabled} />
      </UserContextProvider>,
    ),
  )
}

/**
 * Hydrate my projects header link
 */
function hydrateMyProjectsHeader(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.MY_PROJECTS)
  if (!mount || ssrProps.tenant !== 'vistaprint') return

  const { myProjectsLink, isRedesignedSignInButtonEnabled = false, isLoggedInSsr } = ssrProps

  if (!myProjectsLink) return

  hydrateAndTrack(mount, withAuth(locale, <MyProjects {...{ myProjectsLink, isRedesignedSignInButtonEnabled, isLoggedInSsr }} />))
}

/**
 * Hydrate help header link for tooltip support on smaller screens
 */
function hydrateHelpLink(ssrProps: WebSSRProps): void {
  const mount = getHydrationMount(MountIds.HELP_LINK)
  if (!mount || !ssrProps.helpLink) return

  const { helpLink, carePhoneNumber, siteHeaderHelpLinkA11YText = '' } = ssrProps

  // This is a simple link, hydration is required to show tooltip on icon in csr
  hydrateAndTrack(mount, <HelpCenter helpLink={helpLink} carePhoneNumber={carePhoneNumber} siteHeaderHelpLinkA11YText={siteHeaderHelpLinkA11YText} />)
}

/**
 * Hydrate favorites header link
 */
function hydrateFavoritesHeader(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.FAVORITES_HEADER)
  if (!mount) return

  hydrateAndTrack(mount, withAuth(locale, withI18n(locale, withUserContext(ssrProps, <FavoritesHeader />))))
}

/**
 * Hydrates the shopping cart component
 */
function hydrateCart(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.CART)
  if (!mount || !ssrProps.cartLink) return

  const { cartLink, tenantConfig } = ssrProps
  hydrateAndTrack(mount, withAuth(locale, withTenantConfig(tenantConfig ?? null, <Cart locale={locale} cartLink={cartLink} />)))
}

/**
 * Hydrates the My Account component
 */
function hydrateMyAccount(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.MY_ACCOUNT)
  if (!mount || !ssrProps.accountLink) return

  const { accountLink } = ssrProps
  hydrateAndTrack(
    mount,
    withAuth(
      locale,
      withI18n(
        locale,
        withUserContext(
          ssrProps,
          <MyAccount
            accountLink={accountLink}
            isRedesignedSignInButtonEnabled={ssrProps.isRedesignedSignInButtonEnabled!}
            isLoggedInSsr={ssrProps.isLoggedInSsr}
          />,
        ),
      ),
    ),
  )
}

/**
 * Hydrates country selector buttons (desktop and mobile)
 */
function hydrateCountrySelectors(ssrProps: WebSSRProps, locale: string): void {
  if (!ssrProps.currentCountry || !ssrProps.localeSelectButtonA11yText) return

  const { currentCountry, localeSelectButtonA11yText, tenant, tenantConfig, countrySelectorDialogHeader } = ssrProps
  const CountrySelectorComponent = () =>
    withScreenClass(
      withTenantConfig(
        tenantConfig ?? null,
        withI18n(
          locale,
          <CountrySelectorButton
            currentCountry={currentCountry}
            localeSelectButtonA11yText={localeSelectButtonA11yText}
            locale={locale}
            tenant={tenant}
            countrySelectorDialogHeader={countrySelectorDialogHeader}
          />,
        ),
      ),
    )

  const desktopMount = getHydrationMount(MountIds.COUNTRY_SELECTOR_DESKTOP)
  if (desktopMount) {
    hydrateAndTrack(desktopMount, <CountrySelectorComponent />)
  }

  const mobileMount = getHydrationMount(MountIds.COUNTRY_SELECTOR_MOBILE)
  if (mobileMount) {
    hydrateAndTrack(mobileMount, <CountrySelectorComponent />)
  }
}

/**
 * Hydrates footer navigation sections
 */
function hydrateFooterNavigation(ssrProps: WebSSRProps): void {
  if (!ssrProps.footerNavigation) return

  const { footerNavigation, awardCallout, isLoggedInSsr, locale } = ssrProps

  const fullFooterMount = getHydrationMount(MountIds.FOOTER_NAVIGATION)
  if (fullFooterMount) {
    hydrateAndTrack(
      fullFooterMount,
      withAuth(locale, <FooterNavigation footerNavigation={footerNavigation} awardCallout={awardCallout} isLoggedInSsr={isLoggedInSsr} />),
    )
  }
}

/**
 * Hydrates the Absolute Guarantee section (National Pen logo + modal)
 */
function hydrateAbsoluteGuarantee(ssrProps: WebSSRProps, locale: string): void {
  const { absoluteGuarantee } = ssrProps
  if (!absoluteGuarantee) return

  const mount = getHydrationMount(MountIds.NP_ABSOLUTE_GUARANTEE)
  if (!mount) return

  hydrateAndTrack(mount, withI18n(locale, <NPAbsoluteGuaranteeLogo absoluteGuarantee={absoluteGuarantee} />))
}

/**
 * Fetches navigation data and hydrates desktop/mobile navigation components
 * Uses requestIdleCallback to defer CPU-intensive hydration work
 */
async function hydrateNavigationComponents(ssrProps: WebSSRProps, locale: string): Promise<void> {
  const userVariationKey = ssrProps.navigationData?.userVariationKey
  const desktopMount = getHydrationMount(MountIds.DESKTOP_NAVIGATION)
  const mobileMount = getHydrationMount(MountIds.MOBILE_MENU)

  if (!userVariationKey || (!desktopMount && !mobileMount)) return

  const { myProjectsLink, helpLink, tenant, brands, brandsDesktop, brandsHeading } = ssrProps

  try {
    const navigationData = await fetchNavigationByVariation(locale, userVariationKey, tenant)

    scheduleIdleHydration(() => {
      if (desktopMount) {
        hydrateAndTrack(
          desktopMount,
          withAuth(
            locale,
            withI18n(
              locale,
              <NavContextProvider
                navigation={navigationData.data}
                topLevelNavNodeTests={navigationData.topLevelNavNodeTests || []}
                userTests={ssrProps.navigationData?.abTestData || []}
              >
                <DesktopNavigation navigationData={navigationData.data} />
              </NavContextProvider>,
            ),
          ),
        )
      }

      if (mobileMount) {
        const { vatToggleLabel, vatToggleInclusionText, vatToggleExclusionText } = ssrProps
        hydrateAndTrack(
          mobileMount,
          withAuth(
            locale,
            withI18n(
              locale,
              withUserContext(
                ssrProps,
                <NavContextProvider
                  navigation={navigationData.data}
                  topLevelNavNodeTests={navigationData.topLevelNavNodeTests || []}
                  userTests={ssrProps.navigationData?.abTestData || []}
                >
                  <VatContextProvider {...{ vatToggleExclusionText, vatToggleInclusionText, vatToggleLabel }}>
                    <MobileMenu
                      helpLink={helpLink!}
                      myProjectsLink={myProjectsLink!}
                      navigationData={navigationData.data}
                      brands={brands!}
                      brandsDesktop={brandsDesktop!}
                      locale={locale}
                      brandsHeading={brandsHeading!}
                      isRedesignedSignInButtonEnabled={ssrProps.isRedesignedSignInButtonEnabled!}
                    />
                  </VatContextProvider>
                </NavContextProvider>,
              ),
            ),
          ),
        )
      }
    })
  } catch (error) {
    Logger.instance.error('[bookends-v2] Failed to fetch navigation data', { error })
  }
}

/**
 * Parses tracking data from an element's data-tracking attribute and sends it to the appropriate tracker.
 */
function sendTrackingEvent(element: HTMLElement, event: Event): void {
  const trackingData = element.dataset['tracking']
  if (!trackingData) return

  try {
    const { bookendName, ...data } = JSON.parse(trackingData) as TrackLinkOptions & { bookendName: ComponentType }

    if (!data.href && (event.target as HTMLElement)?.nodeName === 'A') {
      data.href = (event.target as HTMLAnchorElement).href
    }

    const tracker = bookendName === ComponentType.HEADER ? getHeaderTracker() : getFooterTracker()
    tracker.trackLink(data)
  } catch (error) {
    Logger.instance.error('[bookends-v2] Failed to parse tracking data', { error })
  }
}

const boundElements = new WeakSet<HTMLElement>()

/**
 * Sets up tracking for server-side rendered (SSR) elements that have a `data-tracking` attribute.
 * Adds event listeners for click and keyboard (Enter/Space) interactions.
 */
function trackingForSSRElements(): void {
  const ssrElements = document.querySelectorAll<HTMLElement>('[data-tracking]')

  ssrElements.forEach((element) => {
    // Prevent duplicate listener registration when mount is called multiple times (header + footer)
    if (boundElements.has(element)) return
    boundElements.add(element)

    element.addEventListener('click', (event) => {
      sendTrackingEvent(element, event)
    })

    element.addEventListener('keydown', (event) => {
      if (['Enter', ' '].includes(event.key)) {
        sendTrackingEvent(element, event)
      }
    })
  })
}

/**
 * Hydrates the brand pillars carousel for interactive dot navigation.
 * Reads pillar data from ssrProps passed via createMountEvent during SSR.
 */
function hydrateBrandPillarsCarousel(ssrProps: WebSSRProps, locale: string): void {
  const mount = getHydrationMount(MountIds.BRAND_PILLARS_CAROUSEL)
  if (!mount || !ssrProps.pillarData?.length) return

  hydrateAndTrack(
    mount,
    // SwanI18nProvider is needed here because hydrateAndTrack creates a standalone React root
    // that does not inherit SwanProvider (and its i18n context) from the ubik framework.
    // AdaptiveCarousel uses useSwanI18nContext internally for its accessibility labels.
    <SwanI18nProvider locale={locale as never} translations={SWAN_ADAPTIVE_CAROUSEL_TRANSLATIONS}>
      <ScreenClassProvider>
        <BoundedContent>
          <PillarCarousel pillarData={ssrProps.pillarData} carouselA11yText={DEFAULT_CAROUSEL_A11Y} />
        </BoundedContent>
      </ScreenClassProvider>
    </SwanI18nProvider>,
  )
}

/**
 * Client-side mount function that hydrates interactive components
 * SSR renders static HTML, this function adds interactivity where needed
 */
export const mount: MountWithRootOptions<WebSSRProps> = async (_rootElement, ssrProps) => {
  const locale = ssrProps.locale || DEFAULT_LOCALE
  const componentType = getComponentType(ssrProps.componentName)

  // Preload the locale chunk (and its fallback chain) before hydration so
  // the first client render matches the server-rendered HTML.
  await loadLocale(locale.toLowerCase() as Locale)

  // Initialize tracking based on component type
  initTracking(locale, componentType)

  // Hydrate header components
  if (componentType === ComponentType.HEADER) {
    hydrateGoogleOneTap(ssrProps, locale)
    hydrateGeoIpBar(ssrProps, locale)
    hydrateVatSelectionBar(ssrProps, locale)
    hydrateLanguageBar(ssrProps, locale)
    hydrateVatToggle(ssrProps)
    hydrateSearchBar(ssrProps, locale)
    hydrateMyProjectsHeader(ssrProps, locale)
    hydrateHelpLink(ssrProps)
    hydrateFavoritesHeader(ssrProps, locale)
    hydrateCart(ssrProps, locale)
    hydrateMyAccount(ssrProps, locale)
    hydrateCountrySelectors(ssrProps, locale)

    // Hydrate navigation (deferred for performance)
    await hydrateNavigationComponents(ssrProps, locale)
  }

  // Hydrate footer components
  if (componentType === ComponentType.FOOTER) {
    hydrateCountrySelectors(ssrProps, locale)
    hydrateFooterNavigation(ssrProps)
    hydrateAbsoluteGuarantee(ssrProps, locale)
    // Hydrate brand pillars carousel (mobile only, for interactive dots)
    hydrateBrandPillarsCarousel(ssrProps, locale)
  }

  // Setup tracking for SSR-only elements
  trackingForSSRElements()

  return {
    unmount: () => {
      // Unmount all tracked hydration roots
      hydrationRoots.forEach((root) => root.unmount())
      hydrationRoots.clear()
    },
  }
}
